import React, { useEffect, useMemo, useRef, useState } from "react";
import { BatchAndExpiryManagementRowsData } from "../../../types/types";
import {
  batchAndExpiryColumnsMetadata,
  getMergedCatalogueAndBatchIdData,
  setBatchWiseStockDataInDb,
  transformToBatchwiseRowsData,
} from "./BatchAndExpiryManagementUtils";
import { Loader } from "../../../components/loader/Loader";
import CustomDataGrid from "../../../components/dms_common/CustomDataGrid";
import Header from "../../../components/dms_common/Header";
import CreateBatchIdForm from "./CreateBatchIdForm";
import {
  defaultSearchFunction,
  filterData,
} from "../../../components/dms_common/DmsCommonServices";
import EmptyPage from "../../claimsNew/components/SelectClaimType";
import realTimeEmptyImage from "../../../assets/image/svg/realTimeEmptyImage.svg";
import { useNavigate } from "react-router-dom";
import { ConfirmationPopUp } from "../../../components/popup/confirmationPopUp";
import { Alert, Snackbar } from "@mui/material";
import { downloadAllReports } from "../../../services/manageReportService";
import dayjs, { Dayjs } from "dayjs";
import { getDataFromLocalDb } from "../../../services/dbService";
import "./BatchAndExpiryManagement.css";
import { getConfigData } from "../../../services/configurationService";
import { Product } from "../../createSecondaryOrder/models";
import ScreenOverlay from "../../../components/dms_common/ScreenOverlay";
import { GenericModal, getGenericModalProps, initialGenericModalProps } from "../../viewOrder/ViewOrderUtils";
import CustomGenericModal from "../../../components/dms_common/CustomGenericModal";
import { logAnalyticsEvent, logLoadTrace } from "../../../firebase";

const BatchAndExpiryManagement = () => {
  const navigate = useNavigate();
  const [confirmPopupState, setConfirmPopupState] = useState<boolean>(false);
  const [openCreateBatchIdForm, setOpenCreateBatchIdForm] = useState(false);
  const [data, setData] = useState<BatchAndExpiryManagementRowsData[]>([]);
  const [rows, setRows] = useState<BatchAndExpiryManagementRowsData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [skuProductDescriptionMap, setSkuProductDescriptionMap] = useState<Record<string, string>>(
    {}
  );
  const [skuUomMap, setSkuUomMap] = useState<Record<string, string>>({});
  const [lastRefershed, setLastRefereshed] = useState<Dayjs>(dayjs());
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const batchwiseExpiryReportRef = useRef<string | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(
    initialGenericModalProps
  );
  const [resetSearch, setResetSearch] = useState(false);

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSearchValueChange = (newValue: string) => {
    setSearchedValue(newValue);
  };

  const filteredData = useMemo(() => {
    return filterData(data, searchedValue.toLowerCase(), defaultSearchFunction);
  }, [searchedValue, data]);

  useEffect(() => {
    setRows(filteredData);
  }, [filteredData]);

  const getInitialTableData = async () => {
    try {
      await setBatchWiseStockDataInDb();
      setLastRefereshed(dayjs());
      const batchWiseStockData = await getDataFromLocalDb("BatchWiseStockData");
      if (batchWiseStockData.data.length > 0) {
        let batchIdWiseStockData: BatchAndExpiryManagementRowsData[] = transformToBatchwiseRowsData(
          batchWiseStockData.data
        );
        const productsData = await getDataFromLocalDb("Product");
        const productsMap: Record<string, Product> = productsData?.data?.[0]?.data ?? {};
        const skuProductMap: Record<string, string> = {};
        const tempSkuUomMap: Record<string, string> = {};
        const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
        Object.values(productsMap).forEach((item) => {
          skuProductMap[item.batchCode] = item.skuDescription;
          tempSkuUomMap[item.batchCode] = ConfigDictionary.orderUomCheck === "true" ? (item?.uom ?? "") : "all";
        });
        setSkuProductDescriptionMap(skuProductMap);
        setSkuUomMap(tempSkuUomMap);
        const mergedData = getMergedCatalogueAndBatchIdData(batchIdWiseStockData, productsMap);
        setRows(mergedData);
        setData(mergedData);
        logAnalyticsEvent( "batch_expiry_management_viewed", {
          "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
          "total_batches_displayed":mergedData.length
        });
        
      } else {
        setRows([]);
        setData([]);
        logAnalyticsEvent( "batch_expiry_management_viewed", {
          "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
          "total_batches_displayed":0
        });
      }
    } catch (error) {
      setIsError(true)
      setRows([]);
      setData([]);
      logAnalyticsEvent( "batch_expiry_management_viewed", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "total_batches_displayed":0
      });
      setGenericModalProps(
        getGenericModalProps(
          "error",
          `Error while fetching batch wise stock data`
        )
      );
    }
  };

  const init = async () => {
    setResetSearch(true); 
    setTimeout(() => setResetSearch(false), 0); 
    setIsLoading(true);
    await getInitialTableData();
    const batchwiseExpiryReportIdConfig = await getConfigData(
      "dms_configuration",
      "batchwiseExpiryReportId"
    );
    batchwiseExpiryReportRef.current = batchwiseExpiryReportIdConfig[0]?.configValue ?? "";
    setIsLoading(false);
  };

  useEffect(() => {
    setSearchedValue("");
    if (!openCreateBatchIdForm) {
      logLoadTrace(init,"batch_expiry_management_page_load_time");
    }
  }, [openCreateBatchIdForm]);

  useEffect(() => {
    const interval = setInterval(() => {
      logLoadTrace(init,"batch_expiry_management_page_load_time");
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="BatchAndExpiryManagement">
      <ScreenOverlay apply={isLoading} />
      {!openCreateBatchIdForm ? (
        <>
          <Header
            title={"Batch & Expiry Management"}
            searchFunction={handleSearchValueChange}
            onRefresh={init}
            createBtnLabel={"Create New Batch ID"}
            handleCreateBtnClick={() => {
              setOpenCreateBatchIdForm(true);
              logAnalyticsEvent( "batch_creation_initiated", {
                "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
              });
            }}
            downloadReportIcon={true}
            downloadReportOnClick={() => {
                logAnalyticsEvent( "batch_expiry_report_downloaded", {
                  "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
                });
                downloadAllReports(batchwiseExpiryReportRef.current, setSnackbarOpen)
              }
            }
            lastRefreshed={lastRefershed}
            resetSearch={resetSearch}
          />
          {isLoading ? (
            <Loader sx={{ position: "absolute", top: "45%" }} />
          ) : isError ? 
          <CustomGenericModal
          type={genericModalProps?.type}
          message={genericModalProps?.message}
          modalOpen={genericModalProps?.open}
          onClose={() => setGenericModalProps(initialGenericModalProps)}
        />
          : rows.length === 0 && searchedValue.length === 0 ? (
            <EmptyPage
              height={87}
              text={
                <div style={{ textAlign: "center", whiteSpace: "pre-wrap" }}>
                  No Stock Data Available.
                  <br />
                  Click Below To Add Stock Data
                </div>
              }
              imgSrc={realTimeEmptyImage}
              button={
                <button
                  className="button-save-claim"
                  style={{ height: "34px", width: "125px", marginTop: "10px" }}
                  onClick={() => {
                    setConfirmPopupState(true);
                  }}
                >
                  <span className="button-save-text-claim">+ ADD</span>
                </button>
              }
            />
          ) : (
            <CustomDataGrid
              searchedValue={searchedValue}
              rows={rows}
              columnsMetaData={batchAndExpiryColumnsMetadata}
              tableWidth={0.79}
              showShelfLifeFooter={true}
              noDataMessage="No Stock data found."
              enableRowClickRedirect={false}
            />
          )}
        </>
      ) : (
        <CreateBatchIdForm
          setOpenCreateBatchIdForm={setOpenCreateBatchIdForm}
          skuProductDescriptionMap={skuProductDescriptionMap}
          skuUomMap={skuUomMap}
        />
      )}
      <ConfirmationPopUp
        message={"You're about to be redirected to MDM page. Click Continue to proceed."}
        openConfirmModal={confirmPopupState}
        successMethod={() => {
          navigate("/dashboard/mdm2");
        }}
        setOpenConfirmModal={setConfirmPopupState}
      />
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your report will be downloaded shortly.
        </Alert>
      </Snackbar>
    </div>
  );
};

export default BatchAndExpiryManagement;
